import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  query,
  orderBy
} from 'firebase/firestore';

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [search, setSearch] = useState('');
  const [monthFilter, setMonthFilter] = useState('all');
  const [sortKey, setSortKey] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPayouts = async () => {
      try {
        const q = query(collection(db, 'payouts'), orderBy('timestamp', 'desc'));
        const snapshot = await getDocs(q);
        setPayouts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error('❌ Error fetching payouts:', err);
      }
    };
    fetchPayouts();
  }, []);

  const filtered = payouts.filter(payout => {
    const matchesUser = (payout.ownerId || '').toLowerCase().includes(search.toLowerCase());
    const matchesMonth = monthFilter === 'all' ||
      (payout.timestamp &&
        new Date(payout.timestamp.seconds * 1000).toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        }) === monthFilter);
    return matchesUser && matchesMonth;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    const valA = sortKey === 'amount' ? (a.amount || 0) : (a.timestamp?.seconds || 0);
    const valB = sortKey === 'amount' ? (b.amount || 0) : (b.timestamp?.seconds || 0);
    return sortOrder === 'asc' ? valA - valB : valB - valA;
  });

  const exportCSV = () => {
    const header = ['ID', 'Owner ID', 'Amount', 'Currency', 'Status', 'Last4', 'Timestamp'];
    const rows = sorted.map(p => [
      p.id,
      p.ownerId,
      p.amount || 0,
      p.currency || 'USD',
      p.status || '',
      p.bankLast4 || '',
      p.timestamp?.seconds ? new Date(p.timestamp.seconds * 1000).toISOString() : ''
    ]);
    const csvContent = [header, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'payouts.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalEarnings = sorted.reduce((sum, p) => sum + (p.amount || 0), 0);

  const uniqueMonths = [...new Set(payouts.map(p =>
    p.timestamp ? new Date(p.timestamp.seconds * 1000).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    }) : null
  ).filter(Boolean))];

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>← Back to Dashboard</button>
      <h2 style={styles.title}>💸 Payout History (Admin)</h2>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Filter by user ID"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={styles.search}
        />
        <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)} style={styles.search}>
          <option value="all">All Months</option>
          {uniqueMonths.map(month => (
            <option key={month} value={month}>{month}</option>
          ))}
        </select>
        <button onClick={exportCSV} style={styles.backButton}>Export CSV</button>
      </div>

      <div style={{ marginBottom: '1rem', fontWeight: '600', color: '#065f46' }}>
        Total Earnings: ${totalEarnings.toFixed(2)} ({sorted.length} payouts)
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Payout ID</th>
            <th style={styles.th}>Owner ID</th>
            <th style={styles.th}>
              <button onClick={() => toggleSort('amount')} style={styles.thButton}>Amount</button>
            </th>
            <th style={styles.th}>Currency</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Last4</th>
            <th style={styles.th}>
              <button onClick={() => toggleSort('timestamp')} style={styles.thButton}>Timestamp</button>
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '1rem' }}>No payouts found</td></tr>
          ) : (
            sorted.map(payout => (
              <tr key={payout.id} style={styles.row}>
                <td style={styles.td}>{payout.id}</td>
                <td style={styles.td}>{payout.ownerId}</td>
                <td style={styles.td}>${(payout.amount || 0).toFixed(2)}</td>
                <td style={styles.td}>{payout.currency || 'USD'}</td>
                <td style={styles.td}>{payout.status}</td>
                <td style={styles.td}>{payout.bankLast4 || '—'}</td>
                <td style={styles.td}>
                  {payout.timestamp?.seconds
                    ? new Date(payout.timestamp.seconds * 1000).toLocaleString()
                    : '—'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  backButton: {
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    height: '2.5rem'
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#065f46',
    marginBottom: '1rem',
  },
  search: {
    padding: '0.5rem',
    fontSize: '0.95rem',
    maxWidth: '240px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    backgroundColor: '#e0f2f1',
    fontWeight: '600',
    color: '#000',
    fontSize: '0.875rem',
  },
  td: {
    padding: '12px',
    fontSize: '0.875rem',
    color: '#333',
  },
  row: {
    borderBottom: '1px solid #eee',
  },
  thButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontWeight: 600,
    cursor: 'pointer',
    color: '#000'
  },
};
